import { CKButton, CKCompanyLogo, CKLayoutHorizontal } from 'clearkit';
import { useState } from 'react';

import { AuthLayoutHeader } from '~/components/layouts/AuthLayout';
import { authUser } from '~/lib/client';

import { AuthBox } from '../../AuthBox';
import useDiscoveryLogin from '../../shared/useDiscoveryLogin';
import { Account, DiscoveredAccount } from '../types';

type ListOrganizationsProps = {
  accounts: DiscoveredAccount[];
};

export default function ListOrganizations({
  accounts,
}: ListOrganizationsProps) {
  const { mutateAsync, isLoading } = useDiscoveryLogin();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>();

  async function login(account: Account) {
    if (isLoading) {
      return;
    }

    setSelectedAccount(account);
    try {
      const { isSuccess, error } = await mutateAsync(account.id);

      if (isSuccess) {
        window.location.href = '/';
      } else {
        authUser(error);
      }
    } finally {
      setSelectedAccount(null);
    }
  }

  const sortedAccounts = [...accounts].sort((a, b) =>
    a.account.name.localeCompare(b.account.name),
  );

  return (
    <>
      <AuthLayoutHeader
        description="Pick an account to continue."
        header={
          <div className="w-full font-semibold text-center">
            Choose your Clearbit account
          </div>
        }
      />
      <AuthBox>
        <ul className="flex flex-col gap-6">
          {sortedAccounts.map((discovered, idx) => (
            <CKLayoutHorizontal as="li" key={idx}>
              <span>
                <CKCompanyLogo
                  size={24}
                  src={`https://logo.clearbit.com/${discovered.account.domain}`}
                />
              </span>
              <span className="flex-grow text-base font-medium text-left">
                {discovered.account.name}
              </span>

              <CKButton
                isLoading={discovered.account === selectedAccount}
                onClick={() => login(discovered.account)}
                variant="tertiary"
                variantColor="blue"
              >
                {actionLabel(discovered)}
              </CKButton>
            </CKLayoutHorizontal>
          ))}
        </ul>
      </AuthBox>
    </>
  );
}

function actionLabel(discovered: DiscoveredAccount) {
  switch (discovered.membership_type) {
    case 'invited_member':
      return 'Accept invite & sign in';
    case 'active_member':
      return 'Sign in';
    case 'pending_member':
      return 'Join & sign in';
    case 'eligible_to_join_by_email_domain':
      return 'Join & sign in';
    default:
      return 'Sign in';
  }
}
